import "./ContactSearchSelectAdd.scss";
import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import encryptedapiRequest from "../../lib/encryptedapiRequest";
import ClientContactCard from "./../../routes/clients/ClientContactCard";

const ContactSearchSelectAdd = forwardRef(
  ({ 
    selectedContacts,
    setSelectedContacts, 
    onAddNew, 
    onSelectedContactClick,    // when user clicks on any link of selected contact
    clientId, 
    limitSelected = null // 🔹 default = null = unlimited
  }, ref) => {

  /**
   * search → user typed search text
   * masterContacts → master contacts fetched from server
   * loading → API loading state
   */

    const [search, setSearch] = useState("");
    const [masterContacts, setmasterContacts] = useState([]);
    const [loading, setLoading] = useState(false);


  /**
   * FETCH CONTACTS
   * ----------------
   * Load master contacts list when clientId changes
   */

    // 🔥 Fetch contacts if not provided
    useEffect(() => {
      if (!clientId) return;

      const fetchContacts = async () => {
        try {
          setLoading(true);
          const res = await encryptedapiRequest.get(
            `/clients/contacts/?clientid=${clientId}`
          );

          // console.log("Master Client Contacts:", res.data.data);

          initializeContacts(res.data.data || []);
        } catch (err) {
          console.error("Failed to fetch client contacts", err);
        } finally {
          setLoading(false);
        }
      };
      fetchContacts();
    }, [clientId]);


   /**
   * INITIALIZE CONTACTS
   * -------------------
   * We store master contacts here.
   * These are NOT selection state.
   */

  const initializeContacts = (allContacts) => {
    const contactsArray = Array.isArray(allContacts) ? allContacts : [];
    setmasterContacts(contactsArray);
  };



  /**
   * RESOLVE CONTACTS BY ID
   * ----------------------
   * Parent may send selectedContacts as only {id,status}
   * This function hydrates them using master contacts.
   */

  const resolveContactsById = (incoming, allContacts) => {
      if (!Array.isArray(incoming) || !Array.isArray(allContacts)) return incoming;
      // console.log("resolving contacts by id",incoming,allContacts);

      return incoming.map(item => {
        const id = Number(item?.id ?? item);
        const found = allContacts.find(c => Number(c.id) === id);



        // ❗ keep original if not found
        if (!found) {
          // console.log("contact not found");
          return {
            ...item,
            status: item?.status ?? "default"
          };
        }
        // console.log("found contact",found,item);
        return {
          ...found,
          ...item,
          status: item?.status ?? "default"
        };
      });
    };



   /**
   * HYDRATE SELECTED CONTACTS
   * -------------------------
   * After contacts load, ensure selectedContacts contain
   * full contact information.
   */
  useEffect(() => {
    // wait until master contacts are loaded
    if (masterContacts.length === 0) return;

    // nothing to resolve
    if (!Array.isArray(selectedContacts) || selectedContacts.length === 0) return;

    // already resolved → do nothing
    if (selectedContacts.every(sc => sc.contactname)) return;

    const resolved = resolveContactsById(selectedContacts, masterContacts);
    // console.log("resolved contacts", resolved);

    // ❗ IMPORTANT: never overwrite parent state with empty array
    if (resolved.length > 0) {
      setSelectedContacts(resolved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterContacts]);



    // const [pendingNewContacts, setPendingNewContacts] = useState([]);

    // useEffect(() => {
    //   if (contacts.length === 0) return;
    //   if (pendingNewContacts.length === 0) return;

    //   // console.log("Processing pending contacts AFTER contacts loaded");

    //   setSelectedContacts(prev => {
    //     let updated = [...prev];

    //     pendingNewContacts.forEach(newContact => {
    //       // if (!newContact) return;   // prevent crash
    //       const id = Number(newContact.id);
    //       const exists = updated.find(c => Number(c.id) === id);

    //       if (exists) {
    //         updated = updated.map(c =>
    //           Number(c.id) === id
    //             ? { ...c, ...newContact, status: "add" }
    //             : c
    //         );
    //       } else {
    //         updated.push({
    //           ...newContact,
    //           id,
    //           status: "add"
    //         });
    //       }
    //     });

    //     return updated;
    //   });

    //   setPendingNewContacts([]);

    // }, [contacts]);

    

  /**
   * SEARCH MATCHING
   * ----------------
   * Checks whether a contact matches the search query.
   */  


    // Search filter
    const matchesSearch = (contact, query) => {
      if (!query) return false;
      const q = query.toLowerCase();

      const numbers = contact.contactnumbers
        ? JSON.parse(contact.contactnumbers)
            .map((n) => n.number)
            .join(" ")
        : "";

      const emails = contact.contactemails
        ? JSON.parse(contact.contactemails)
            .map((e) => e.email)
            .join(" ")
        : "";

      const nameMatch = (contact.contactname || "").toLowerCase().includes(q);
      const typeMatch = (contact.contacttypes || "").toLowerCase().includes(q);
      const colonyMatch = (contact.contactcolonies || "")
        .toLowerCase()
        .includes(q);
      const numberMatch = numbers.toLowerCase().includes(q);
      const emailMatch = emails.toLowerCase().includes(q);

      return nameMatch || typeMatch || colonyMatch || numberMatch || emailMatch;
    };



   /**
   * SUGGESTIONS
   * ------------
   * Suggestions exclude contacts already selected
   * (unless they were marked as deleted).
   */

    // Suggestions (excluding deleted or already selected)
    const suggestions = masterContacts
      .filter((c) => matchesSearch(c, search))
      .filter((c) => {
        // 🔹 NEW LOGIC: exclude contacts that are in selectedContacts with status != "delete"
        // const isSelectedAndActive = selectedContacts.some(
        //   (sc) => sc.id === c.id && sc.status !== "delete"
        // );
        // return !isSelectedAndActive;

        return !selectedContacts.some(
          (sc) => Number(sc.id) === Number(c.id) && sc.status !== "delete"
        );


      })
      .slice(0, 4);



 /**
   * ADD NEW CONTACT INTERNAL
   * -------------------------
   * Called when parent creates a new contact on server.
   *
   * Steps:
   * 1) Ensure contact exists in master contacts list
   * 2) Add/update it in selectedContacts
   */      


  const addNewContactInternal = (newContact) => {

    const id = Number(newContact.id);

    /**
     * Step 1
     * Ensure master contacts list includes the new contact
     */
    setmasterContacts(prev => {

      const exists = prev.find(c => Number(c.id) === id);

      if (exists) return prev;

      return [...prev, newContact];

    });



    /**
     * Step 2
     * Update selectedContacts
     */
    setSelectedContacts(prev => {

      const existing = prev.find(c => Number(c.id) === id);

      if (existing) {

        return prev.map(c =>
          Number(c.id) === id
            ? {
                ...c,
                ...newContact,
                status: "add"
              }
            : c
        );

      }

      return [
        ...prev,
        {
          ...newContact,
          id,
          status: "add"
        }
      ];

    });

  };




// const addNewContactInternal = (newContact) => {
//   setSelectedContacts(prev => {
//     const id = Number(newContact.id);
//     const existing = prev.find(c => Number(c.id) === id);

//     // 🔹 If contact already exists → update it
//     if (existing) {
//       return prev.map(c =>
//         Number(c.id) === id
//           ? {
//               ...c,
//               ...newContact,      // update fields
//               status: "add"       // always ensure active
//             }
//           : c
//       );
//     }

//     // 🔹 If not exists → add it
//     return [
//       ...prev,
//       {
//         ...newContact,
//         id,
//         status: "add"
//       }
//     ];
//   });
// };


 /**
   * HANDLE SELECT CONTACT
   * ---------------------
   * Adds contact to selectedContacts or reactivates it
   * if previously deleted.
   */


const handleSelectContact = (contact) => {

  // console.log("already selected contacts",selectedContacts);

// If limit is defined and already reached, ignore new selection
  if (limitSelected && selectedContacts.filter(c => c.status !== "delete").length >= limitSelected) {
    console.warn(`Selection limit of ${limitSelected} reached`);
    return;
  }

  const exists = selectedContacts.find(sc => sc.id === contact.id);

  let updated;

  if (exists) {
    updated = selectedContacts.map(sc =>
      sc.id === contact.id ? { ...sc, status: "add" } : sc
    );
  } else {
    updated = [...selectedContacts, { ...contact, status: "add" }];
  }

  setSelectedContacts(updated);
  setSearch("");
};











  /**
   * REMOVE CONTACT
   * --------------
   * Marks contact as deleted but keeps it in state
   * so user can reselect it later.
   */


  const handleRemoveContact = (contact) => {
    // console.log("delting contact",contact,selectedContacts);
    
    const updated = selectedContacts.map(sc =>
      sc.id === contact.id ? { ...sc, status: "delete" } : sc
    );

    // console.log("now Deleted",updated);

    setSelectedContacts(updated);
  };



  /**
   * EXPOSE FUNCTIONS TO PARENT
   */


    // Expose getAllContacts via ref
    useImperativeHandle(ref, () => ({
      getAllContacts: () => masterContacts,
      getAllSelectedContacts: () => selectedContacts,
      
      // addNewContact: (newContact) => {
      // //  console.log("addNewContact called with:", newContact);
      // //  console.log("contacts length:", contacts.length);
      //   if (contacts.length === 0) {
      //       // Not initialized yet → queue it
      //       setPendingNewContacts(prev => [...prev, newContact]);
      //       return;
      //     }
      //     addNewContactInternal(newContact);
      //   },

    /**
     * Parent calls this after new contact is created on server
     */
    addNewContact: (newContact) => {
      // console.log("[Child] addNewContact invoked:", newContact);
      // console.log("[Child] current selectedContacts before add:", selectedContacts);

      addNewContactInternal(newContact);
      // console.log("[Child] selectedContacts after add:", selectedContacts);
      // console.log("[Child] masterContacts after add:", masterContacts);

    }



    }));

    return (
      <div className="contactSearchWrapper">
        {/* SEARCH AREA */}
        <div className="searchArea">
          <div className="searchRow">
            <input
              type="text"
              placeholder="Search contact..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault(); // Prevent form submission
                  e.stopPropagation(); // Prevent bubbling outside this input
                  onAddNew?.(e); // Trigger your Add contact logic
                }
              }}
            />
            <button onClick={onAddNew}>➕ Add</button>
          </div>

          {search && suggestions.length > 0 && (
            <div className="suggestions">
              {suggestions.map((contact) => (
                <ClientContactCard
                  key={contact.id}
                  clientcontact={contact}
                  mode="select"
                  onClick={handleSelectContact}
                />
              ))}
            </div>
          )}
        </div>

        {/* SELECTED CONTACTS */}
        <div className="selectedContacts">
          {selectedContacts
            .filter(
              (contact) =>
                contact.status === "default" || contact.status === "add",
            ) // ✅ CHANGED: show only default/add
            .map((contact) => (
              <div
                className="selectedCardWrapper"
                key={contact.id}
                onClick={() => onSelectedContactClick?.(contact)}
              >
                <ClientContactCard
                  clientcontact={contact}
                  mode="select"
                  // showCheckbox
                />
                <span
                  className="removeIcon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveContact(contact);
                  }}
                >
                  ✕
                </span>
              </div>
            ))}
        </div>
      </div>
    );
  }
);

export default ContactSearchSelectAdd;
